/**
 * Bottom-left badge shown while highlight mutations wait in the offline
 * queue. Drains the queue on mount / on window `online` when there are
 * pending ops, and refreshes its count on the queue's window events.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import {
  getPendingCount,
  getLegacyOperationCount,
  getBlockedOperations,
  processQueue,
  parseCourseHighlightRef,
  type CourseCreateQueueData,
  type ProjectFilePutQueueData,
  type QueueHandlers,
} from '../../lib/offline-queue.js';
import { courseService } from '../../services/courses.js';
import type { HighlightUpdateInput } from '../../services/courses.js';
import { podcastService } from '../../services/podcast.js';
import { auth } from '../../services/auth.js';
import { projectsApi } from '../../services/projects.js';
import type { PodcastHighlight, PodcastThought } from '@xdface-vault/shared';

// Exported for tests: the drain wiring between queued ops and the services.
export function buildHandlers(ownerId?: string): QueueHandlers {
  return {
    courseCreate: (data) => {
      const d = data as CourseCreateQueueData;
      return ownerId
        ? courseService.createHighlight(d.courseId, d.lessonNum, d.input, ownerId)
        : courseService.createHighlight(d.courseId, d.lessonNum, d.input);
    },
    courseDelete: async (hlId) => {
      const ref = parseCourseHighlightRef(hlId);
      if (!ref) {
        // Malformed refs can never succeed — fail as 4xx so the op is dropped
        throw Object.assign(new Error(`Invalid highlight ref: ${hlId}`), { status: 400 });
      }
      if (ownerId)
        await courseService.deleteHighlight(ref.courseId, ref.lessonNum, ref.hlId, ownerId);
      else await courseService.deleteHighlight(ref.courseId, ref.lessonNum, ref.hlId);
    },
    courseUpdate: async (hlId, patch) => {
      const ref = parseCourseHighlightRef(hlId);
      if (!ref) {
        // Malformed refs can never succeed — fail as 4xx so the op is dropped
        throw Object.assign(new Error(`Invalid highlight ref: ${hlId}`), { status: 400 });
      }
      await courseService.updateHighlight(
        ref.courseId,
        ref.lessonNum,
        ref.hlId,
        patch as HighlightUpdateInput,
        ...(ownerId ? [ownerId] : []),
      );
    },
    podcastReplace: async (podcastId, highlights, thoughts) => {
      await podcastService.update(
        podcastId,
        {
          highlights: highlights as PodcastHighlight[],
          ...(thoughts ? { thoughts: thoughts as PodcastThought[] } : {}),
        },
        ownerId,
      );
    },
    projectFilePut: async ({
      projectId,
      path,
      content,
      expectedVersion,
    }: ProjectFilePutQueueData) => {
      await projectsApi.putFile(projectId, path, content, expectedVersion, ownerId);
    },
  };
}

export function SyncPendingBadge() {
  const qc = useQueryClient();
  const [pending, setPending] = useState(0);
  const [legacy, setLegacy] = useState(0);
  const [blocked, setBlocked] = useState(0);
  const [unavailable, setUnavailable] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const draining = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const refresh = async (): Promise<number> => {
      try {
        const [n, old, rejected] = await Promise.all([
          getPendingCount(),
          getLegacyOperationCount(),
          getBlockedOperations(),
        ]);
        if (!cancelled) {
          setPending(n);
          setLegacy(old);
          setBlocked(rejected.length);
          setUnavailable(false);
        }
        return n;
      } catch {
        if (!cancelled) setUnavailable(true);
        return 0;
      }
    };

    const drainIfOnline = async () => {
      const owner = auth.getUserId();
      const n = await refresh();
      if (!owner || auth.getUserId() !== owner || n === 0 || !navigator.onLine || draining.current)
        return;
      draining.current = true;
      if (!cancelled) setSyncing(true);
      try {
        const applied = await processQueue(buildHandlers(owner), owner);
        if (applied > 0 && !cancelled) {
          // Highlight reads live under the ['courses', ...] keys; project file
          // puts under ['projects', ...] / ['project-file', ...]. Podcast
          // highlights use local state (no React Query cache to invalidate).
          qc.invalidateQueries({ queryKey: ['courses'] });
          qc.invalidateQueries({ queryKey: ['projects'] });
          qc.invalidateQueries({ queryKey: ['project-file'] });
        }
      } finally {
        draining.current = false;
        if (!cancelled) {
          setSyncing(false);
          void refresh();
        }
      }
    };

    const onQueuedOrDrained = () => void refresh();
    const onOnline = () => void drainIfOnline();

    void drainIfOnline(); // app startup with pending ops from a previous session
    window.addEventListener('online', onOnline);
    window.addEventListener('vault:hl-queued', onQueuedOrDrained);
    window.addEventListener('vault:queue-drained', onQueuedOrDrained);
    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      window.removeEventListener('vault:hl-queued', onQueuedOrDrained);
      window.removeEventListener('vault:queue-drained', onQueuedOrDrained);
    };
  }, [qc]);

  const exportOperations = async () => {
    try {
      const operations = await getBlockedOperations();
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(operations, null, 2)], { type: 'application/json' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = 'vault-cambios-con-conflicto.json';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setUnavailable(true);
    }
  };

  if (pending === 0 && legacy === 0 && !unavailable) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 left-4 z-40 flex items-center gap-2 rounded-xl bg-surface-2 px-3 py-2 text-sm text-text shadow-soft-lg"
    >
      <RefreshCw className={`h-4 w-4 text-xd-action ${syncing ? 'animate-spin' : ''}`} />
      <div>
        {unavailable && <p>No se pudo consultar la sincronización local.</p>}
        {pending > 0 && <p>{pending} cambio(s) pendiente(s) de sincronizar</p>}
        {blocked > 0 && (
          <p>
            {blocked} cambio(s) requieren revisión.{' '}
            <button type="button" className="underline" onClick={() => void exportOperations()}>
              Descargar cambios
            </button>
          </p>
        )}
        {legacy > 0 && (
          <p>
            {legacy} cambio(s) antiguo(s) sin cuenta identificada permanecen en este navegador y no
            se sincronizarán automáticamente.
          </p>
        )}
      </div>
    </div>
  );
}
